import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import ActionDrawer from '../components/shared/ActionDrawer';
import { showServerError, showSuccess, showError } from '../../utils/toastHelper';

function DiscountCodeModal({ isOpen, onClose, discountCode }) {
  const [formData, setFormData] = useState({
    code: '',
    type: 'PERCENTAGE',
    value: '',
    minPurchaseAmount: '',
    maxDiscountAmount: '',
    startDate: '',
    endDate: '',
    usageLimit: '',
    isActive: true,
    description: ''
  });

  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (discountCode) {
      const formattedStartDate = discountCode.startDate ?
        new Date(discountCode.startDate).toISOString().split('T')[0] : '';

      const formattedEndDate = discountCode.endDate ?
        new Date(discountCode.endDate).toISOString().split('T')[0] : '';

      setFormData({
        code: discountCode.code || '',
        type: discountCode.type || 'PERCENTAGE',
        value: discountCode.value || '',
        minPurchaseAmount: discountCode.minPurchaseAmount || '',
        maxDiscountAmount: discountCode.maxDiscountAmount || '',
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        usageLimit: discountCode.usageLimit || '',
        isActive: discountCode.isActive === undefined ? true : discountCode.isActive,
        description: discountCode.description || '',
        currentUsage: discountCode.currentUsage
      });
    } else {
      resetForm();
    }
  }, [discountCode, isOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resetForm = () => {
    setFormData({
      code: '',
      type: 'PERCENTAGE',
      value: '',
      minPurchaseAmount: '',
      maxDiscountAmount: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      usageLimit: '',
      isActive: true,
      description: '',
      currentUsage: 0
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.code || formData.code.length < 3) {
      newErrors.code = 'Kod en az 3 karakter olmalıdır';
    }
    if (!formData.value || formData.value <= 0) {
      newErrors.value = 'Değer pozitif bir sayı olmalıdır';
    } else if (formData.type === 'PERCENTAGE' && formData.value > 100) {
      newErrors.value = 'Yüzde değeri 100\'den büyük olamaz';
    }
    if (formData.minPurchaseAmount && formData.minPurchaseAmount < 0) {
      newErrors.minPurchaseAmount = 'Minimum tutar negatif olamaz';
    }
    if (formData.maxDiscountAmount && formData.maxDiscountAmount < 0) {
      newErrors.maxDiscountAmount = 'Maksimum indirim tutarı negatif olamaz';
    }
    if (formData.usageLimit && (parseFloat(formData.usageLimit) <= 0 || !Number.isInteger(parseFloat(formData.usageLimit)))) {
      newErrors.usageLimit = 'Kullanım limiti pozitif tam sayı olmalıdır';
    }
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      newErrors.endDate = 'Bitiş tarihi başlangıç tarihinden önce olamaz';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = name === 'code' ? value.toUpperCase() : value;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : finalValue
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(formData).map(([key, val]) => [key, val === '' ? null : val])
      );
      const url = discountCode ? `/admin/discount-codes/${discountCode.id}` : '/admin/discount-codes';
      const method = discountCode ? 'PUT' : 'POST';
      const response = await apiRequest(url, { method, data: payload });
      if (response.success) {
        showSuccess(response.message);
        onClose(true);
      } else {
        showError(response.message);
      }
    } catch (error) {
      if (error.apiError) showServerError();
      else showError(error.message || 'İndirim kodu kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div className="d-flex justify-content-end gap-2 w-100">
      <button type="button" className="btn btn-light" onClick={() => onClose()} disabled={loading}>İptal</button>
      <button type="submit" className="btn btn-dark" disabled={loading} onClick={handleSubmit}>
        {loading ? <><span className="spinner-border spinner-border-sm me-2" />Kaydediliyor...</> : <><i className="fas fa-save me-2" />Kaydet</>}
      </button>
    </div>
  );

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={() => onClose()}
      title={discountCode ? 'İndirim Kodu Düzenle' : 'Yeni İndirim Kodu Ekle'}
      width="700px"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="discount-code-form-content">
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <label className="form-label small fw-medium">Kod*</label>
            <input type="text" className={`form-control form-control-sm ${errors.code ? 'is-invalid' : ''}`} name="code" value={formData.code} onChange={handleChange} disabled={loading || (discountCode && discountCode.currentUsage > 0)} placeholder="Örn: BAHAR2023" maxLength="20" />
            {errors.code && <div className="invalid-feedback small">{errors.code}</div>}
            {discountCode && discountCode.currentUsage > 0 && <small className="text-muted d-block mt-1">Kullanılmış kodlar değiştirilemez</small>}
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label small fw-medium">İndirim Türü*</label>
            <select className="form-select form-select-sm" name="type" value={formData.type} onChange={handleChange} disabled={loading}>
              <option value="PERCENTAGE">Yüzdelik (%)</option>
              <option value="FIXED">Sabit Tutar (TL)</option>
            </select>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-medium">İndirim Değeri*</label>
            <div className="input-group input-group-sm">
              <input type="number" className={`form-control ${errors.value ? 'is-invalid' : ''}`} name="value" value={formData.value} onChange={handleChange} disabled={loading} placeholder={formData.type === 'PERCENTAGE' ? '0-100' : '0'} min="0" max={formData.type === 'PERCENTAGE' ? "100" : ""} step={formData.type === 'PERCENTAGE' ? "1" : "0.01"} />
              <span className="input-group-text">{formData.type === 'PERCENTAGE' ? '%' : 'TL'}</span>
              {errors.value && <div className="invalid-feedback small">{errors.value}</div>}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-medium">Minimum Sepet Tutarı</label>
            <div className="input-group input-group-sm">
              <input type="number" className={`form-control ${errors.minPurchaseAmount ? 'is-invalid' : ''}`} name="minPurchaseAmount" value={formData.minPurchaseAmount} onChange={handleChange} disabled={loading} placeholder="Opsiyonel" min="0" step="0.01" />
              <span className="input-group-text">TL</span>
              {errors.minPurchaseAmount && <div className="invalid-feedback small">{errors.minPurchaseAmount}</div>}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-medium">Maksimum İndirim Tutarı</label>
            <div className="input-group input-group-sm">
              <input type="number" className={`form-control ${errors.maxDiscountAmount ? 'is-invalid' : ''}`} name="maxDiscountAmount" value={formData.maxDiscountAmount} onChange={handleChange} disabled={loading} placeholder="Opsiyonel" min="0" step="0.01" />
              <span className="input-group-text">TL</span>
              {errors.maxDiscountAmount && <div className="invalid-feedback small">{errors.maxDiscountAmount}</div>}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-medium">Başlangıç Tarihi</label>
            <input type="date" className="form-control form-control-sm" name="startDate" value={formData.startDate} onChange={handleChange} disabled={loading} />
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-medium">Bitiş Tarihi</label>
            <input type="date" className={`form-control form-control-sm ${errors.endDate ? 'is-invalid' : ''}`} name="endDate" value={formData.endDate} onChange={handleChange} disabled={loading} />
            {errors.endDate && <div className="invalid-feedback small">{errors.endDate}</div>}
            <small className="text-muted d-block mt-1">Boş bırakılırsa süresiz olur</small>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-medium">Kullanım Limiti</label>
            <input type="number" className={`form-control form-control-sm ${errors.usageLimit ? 'is-invalid' : ''}`} name="usageLimit" value={formData.usageLimit} onChange={handleChange} disabled={loading} placeholder="Opsiyonel" min="1" step="1" />
            {errors.usageLimit && <div className="invalid-feedback small">{errors.usageLimit}</div>}
            <small className="text-muted d-block mt-1">Boş bırakılırsa limitsiz olur</small>
          </div>
          {discountCode && (
            <div className="col-12 col-md-4">
              <label className="form-label small fw-medium">Mevcut Kullanım</label>
              <input type="number" className="form-control form-control-sm" name="currentUsage" value={formData.currentUsage} onChange={handleChange} disabled={loading} min="0" step="1" />
            </div>
          )}
          <div className="col-12 col-md-4">
            <div className="form-check form-switch mt-4">
              <input className="form-check-input" type="checkbox" id="isActive" name="isActive" checked={formData.isActive} onChange={handleChange} disabled={loading} />
              <label className="form-check-label small" htmlFor="isActive">Aktif</label>
            </div>
          </div>
          <div className="col-12">
            <label className="form-label small fw-medium">Açıklama</label>
            <textarea className="form-control form-control-sm" name="description" value={formData.description} onChange={handleChange} disabled={loading} placeholder="İndirim kodu hakkında açıklama (opsiyonel)" rows={isMobile ? "2" : "3"}></textarea>
          </div>
        </div>
      </form>

      <style>{`
        .discount-code-form-content { padding: 0.5rem 0; }

      `}</style>
    </ActionDrawer>
  );
}

export default DiscountCodeModal; 